import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { LayoutDashboard, LogOut } from "lucide-react";

export function Header() {
  const { t } = useI18n();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <header
      className="sticky top-0 z-40 w-full border-b border-slate-200/70 bg-white/80 backdrop-blur-xl"
      data-testid="public-header"
    >
      <div className="mx-auto flex h-14 max-w-7xl items-center justify-between gap-2 px-3 sm:h-16 sm:px-6 lg:px-10">
        <Link to="/" data-testid="header-home-link" className="flex min-w-0 items-center">
          <Logo size="md" />
        </Link>

        <nav className="hidden items-center gap-8 md:flex">
          <NavLink
            to="/#features"
            data-testid="nav-features"
            className="link-underline text-sm font-medium text-slate-600 hover:text-[#0B0E5C]"
          >
            {t("common.features")}
          </NavLink>
          <NavLink
            to="/pricing"
            data-testid="nav-pricing"
            className="link-underline text-sm font-medium text-slate-600 hover:text-[#0B0E5C]"
          >
            {t("common.pricing")}
          </NavLink>
        </nav>

        <div className="flex flex-shrink-0 items-center gap-1 sm:gap-2">
          <LanguageSwitcher />
          {user ? (
            <>
              <Button
                asChild
                variant="ghost"
                size="sm"
                className="rounded-full px-2 sm:px-3"
                data-testid="header-dashboard-btn"
              >
                <Link to="/dashboard">
                  <LayoutDashboard className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">{t("common.dashboard")}</span>
                </Link>
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={async () => {
                  await logout();
                  navigate("/");
                }}
                data-testid="header-logout-btn"
                className="rounded-full"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <>
              <Button
                asChild
                variant="ghost"
                size="sm"
                className="hidden rounded-full sm:inline-flex"
                data-testid="header-signin-btn"
              >
                <Link to="/auth">{t("common.sign_in")}</Link>
              </Button>
              <Button
                asChild
                size="sm"
                className="lime-glow rounded-full bg-[#9ACD32] px-3 font-bold text-[#05073B] hover:bg-[#A8DF38] sm:px-4"
                data-testid="header-signup-btn"
              >
                <Link to="/auth?mode=signup">{t("common.get_started")}</Link>
              </Button>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
